import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Wand2, RefreshCcw, Square, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Label } from "../../components/ui/Label";
import { Checkbox } from "../../components/ui/Checkbox";
import { GenerationProgress } from "../../components/admin/GenerationProgress";
import { useGenerationStream } from "../../hooks/useAdminStream";
import { useAiConfig } from "../../hooks/useAdminApi";

export const AdminGeneratePage: React.FC = () => {
  const [tagName, setTagName] = useState("");
  const [regenerate, setRegenerate] = useState(false);
  const { data: aiConfig } = useAiConfig();
  const stream = useGenerationStream();

  const cost = useMemo(() => {
    if (!stream.usage || !stream.provider) return null;
    const provider = aiConfig?.providers.find(
      (candidate) => candidate.name === stream.provider
    );
    const { pricePerMTokenIn, pricePerMTokenOut } = provider ?? {};
    if (
      typeof pricePerMTokenIn !== "number" ||
      typeof pricePerMTokenOut !== "number" ||
      typeof stream.usage.inputTokens !== "number" ||
      typeof stream.usage.outputTokens !== "number"
    ) {
      return null;
    }
    return (
      (stream.usage.inputTokens / 1_000_000) * pricePerMTokenIn +
      (stream.usage.outputTokens / 1_000_000) * pricePerMTokenOut
    );
  }, [stream.usage, stream.provider, aiConfig]);

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = tagName.trim();
    if (!trimmed) return;
    stream.start(
      `?tag=${encodeURIComponent(trimmed)}${regenerate ? "&regenerate=1" : ""}`
    );
  };

  return (
    <section className="space-y-10">
      <header>
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2">
          Geração
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Gerar releases
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Dispare a geração para uma tag específica ou sincronize todas as tags
          pendentes. O progresso aparece aqui em tempo real.
        </p>
      </header>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="font-medium text-foreground mb-1 flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          Por tag específica
        </h2>
        <p className="text-sm text-muted-foreground mb-5">
          Compara a tag informada com a anterior para gerar o resumo.
        </p>

        <form onSubmit={handleGenerate} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="tagname">Tag</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                id="tagname"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                placeholder="v1.2.0"
                className="font-mono sm:flex-1"
                disabled={stream.isRunning}
              />
              {stream.isRunning ? (
                <Button type="button" variant="secondary" onClick={stream.abort}>
                  <Square className="w-3.5 h-3.5" aria-hidden="true" />
                  Cancelar
                </Button>
              ) : (
                <Button type="submit" disabled={!tagName.trim()}>
                  Gerar
                </Button>
              )}
            </div>
          </div>

          <label
            htmlFor="regenerate"
            className="flex items-start gap-2.5 text-sm text-muted-foreground cursor-pointer select-none leading-relaxed"
          >
            <Checkbox
              id="regenerate"
              checked={regenerate}
              onCheckedChange={(v) => setRegenerate(v === true)}
              className="mt-0.5"
              disabled={stream.isRunning}
            />
            <span>
              <span className="text-foreground font-medium">Regenerar</span>:
              substituir o conteúdo existente desta tag por um novo, gerado a
              partir dos commits. A release só é trocada se a geração der certo.
            </span>
          </label>
        </form>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="font-medium text-foreground mb-1 flex items-center gap-2">
          <RefreshCcw
            className="w-4 h-4 text-muted-foreground"
            aria-hidden="true"
          />
          Sincronizar todas as tags
        </h2>
        <p className="text-sm text-muted-foreground mb-5">
          Busca tags do GitHub que ainda não têm changelog publicado e gera o que
          faltar, em ordem cronológica.
        </p>
        <Button
          variant="secondary"
          onClick={() => stream.start("?all=1")}
          disabled={stream.isRunning}
        >
          {stream.isRunning ? "Em andamento…" : "Iniciar sincronização"}
        </Button>
      </div>

      <GenerationProgress
        isRunning={stream.isRunning}
        tag={stream.tag}
        phase={stream.phase}
        provider={stream.provider}
        envelope={stream.envelope}
        markdown={stream.markdown}
        usage={stream.usage}
        error={stream.error}
        errorDetail={stream.errorDetail}
        notices={stream.notices}
        completed={stream.completed}
        cost={cost}
      />

      {!stream.isRunning && stream.markdown && stream.tag && (
        <Link
          to={`/changelog/${encodeURIComponent(stream.tag)}`}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          Ver o changelog publicado de {stream.tag}
        </Link>
      )}
    </section>
  );
};
